import React, { useState, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './Exhibit.scss';
import './CommonCss.scss';

const Exhibit = ({onClose,onSuccess,editExhibit=null,exhibits,setExhibits})=> {
const [errors, setErrors] = useState({});
const [isSubmitting, setIsSubmitting] = useState(false);

// Edit mode states
const [isEditMode, setIsEditMode] = useState(!!editExhibit);

// Quill editor configuration
const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['clean']
    ],
};

const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'blockquote', 'code-block',
    'list', 'bullet', 'indent',
    'direction', 'align',
    'link', 'image', 'video'
];

// Handle image upload for a specific exhibit
const handleImageAdd = (exhibitId, files) => {
    setExhibits(prev => prev.map(exhibit => {
        if (exhibit.id === exhibitId) {
            const newImages = Array.from(files).map((file, index) => ({
                id: `new_${exhibitId}_${Date.now()}_${index}`,
                file: file,
                preview: URL.createObjectURL(file),
                name: file.name,
                type: file.type,
                size: file.size,
                isExisting: false
            }));
            return {
                ...exhibit,
                images: [...exhibit.images, ...newImages]
            };
        }
        return exhibit;
    }));
};

// Handle image removal for a specific exhibit
const handleImageRemove = (exhibitId, imageId) => {
    setExhibits(prev => prev.map(exhibit => {
        if (exhibit.id === exhibitId) {
            const imageToRemove = exhibit.images.find(img => img.id === imageId);
            if (imageToRemove) {
                // Only revoke URL for new images (not existing ones)
                if (!imageToRemove.isExisting && imageToRemove.preview) {
                    URL.revokeObjectURL(imageToRemove.preview);
                }
            }
            return {
                ...exhibit,
                images: exhibit.images.filter(img => img.id !== imageId)
            };
        }
        return exhibit;
    }));
};

const handleExhibitNameChange = useCallback((exhibitId, value) => {
    setExhibits(prev => prev.map(exhibit => {
        if (exhibit.id === exhibitId) {
            return {
                ...exhibit,
                name: value
            };
        }
        return exhibit;
    }));
}, [setExhibits]);

// Handle textarea content change
const handleTextareaChange = useCallback((exhibitId, textareaId, content) => {
    setExhibits(prev => prev.map(exhibit => {
        if (exhibit.id === exhibitId) {
            return {
                ...exhibit,
                textareas: exhibit.textareas.map(textarea => textarea.id === textareaId ? { ...textarea, content } : textarea )
            };
        }
        return exhibit;
    }));
}, []);

// Add new textarea to a specific exhibit
const addTextarea = (exhibitId) => {
    setExhibits(prev => prev.map(exhibit => {
        if (exhibit.id === exhibitId) {
            // Find the highest numeric ID from existing textareas
            const numericIds = exhibit.textareas
            .map(t => {
                const id = typeof t.id === 'string' ? parseInt(t.id) : t.id;
                return isNaN(id) ? 0 : id;
            })
            .filter(id => id > 0);
            const maxId = numericIds.length>0 ? Math.max(...numericIds) : 0;
            const newTextareaId = maxId + 1;
            return {
                ...exhibit, textareas: [...exhibit.textareas, { id: newTextareaId, content: '' }]
            };
        }
        return exhibit;
    }));
};

// Remove textarea from a specific exhibit
const removeTextarea = (exhibitId, textareaId) => {
    setExhibits(prev => prev.map(exhibit => {
        if (exhibit.id === exhibitId) {
            return {
                ...exhibit, textareas: exhibit.textareas.filter(textarea => textarea.id !== textareaId)
            };
        }
        return exhibit;
    }));
};

// Validate exhibit
const validateExhibit = (exhibit) => {
    return exhibit.images.length > 0 || exhibit.textareas.some(textarea => textarea.content.trim() !== '');
};

// Add new exhibit
const addExhibit = () => {
    const maxId = exhibits.length > 0 ? Math.max(...exhibits.map(exhibit => exhibit.id)) : 0;
    const newId = maxId + 1;
    setExhibits(prev => [...prev, {
        id: newId,
        name: '',
        images: [],
        textareas: [{ id: 1, content: '' }],
        isValid: false
    }]);
};

// Remove exhibit
const removeExhibit = (exhibitId) => {
    setExhibits(prev => {
        const exhibitToRemove = prev.find(exhibit => exhibit.id === exhibitId);
        if (exhibitToRemove && exhibitToRemove.images) {
            // Clean up object URLs to prevent memory leaks
            exhibitToRemove.images.forEach(image => {
                if (!image.isExisting && image.preview) {
                    URL.revokeObjectURL(image.preview);
                }
            });
        }
        return prev.filter(exhibit => exhibit.id !== exhibitId);
    });
};

// Cleanup images on unmount
React.useEffect(() => {
    return () => {
        exhibits.forEach(exhibit => {
            if (exhibit.images) {
                exhibit.images.forEach(image => {
                    // Only revoke URLs for new images (not existing ones)
                    if (!image.isExisting && image.preview) {
                        URL.revokeObjectURL(image.preview);
                    }
                });
            }
        });
    };
}, [exhibits]);

return (
<div className="exhibits-content">
    {/* Error message */}
    {errors.exhibits && (
    <div className="exhibit-error-message message-bar alertRedbar">
        <span className="error-icon">⚠️</span>
        <span>{errors.exhibits}</span>
    </div>
    )}

    {/* Exhibits Section */}
    <div className="exhibit-section m-0">
        {exhibits.map((exhibit, index) => (
        <div key={exhibit.id} className="exhibit-item p-3 border mb-3 bg-light rounded-2">
            <div className="exhibit-item-header d-flex justify-content-between align-items-end mb-3 pb-3 border-bottom">
                <div className="exhibit-header-left d-flex flex-column gap-2 me-2 flex-fill">
                    <h4 className="fw-bold text-black text-opacity-75 mb-0 p14">Exhibit {String.fromCharCode(65 + index)}</h4>
                    <input type="text" value={exhibit.name || ''} onChange={(e) => handleExhibitNameChange(exhibit.id, e.target.value)} placeholder="Enter exhibit name" className="exhibit-name-input form-control p14" />
                </div>
                <button type="button" className="exhibit-remove-btn btn btn-danger text-sm btn-custom px-3 py-1 m-0" onClick={() => removeExhibit(exhibit.id)} disabled={exhibits.length === 1} >🗑️ Remove Exhibit</button>
            </div>
            <div className="exhibit-content">
                {/* Image Column */}
                <div className="exhibit-image-column">
                    <label className="exhibit-column-label d-block fw-bold text-black text-opacity-75 mb-2 p14">Images</label>
                    <div className="exhibit-image-upload mb-2">
                        <input type="file" id={`exhibit-images-${exhibit.id}`} multiple accept="image/*" onChange={(e) => handleImageAdd(exhibit.id, e.target.files)} className="exhibit-file-input d-none" />
                        <label htmlFor={`exhibit-images-${exhibit.id}`} className="exhibit-file-label btn btn-primary text-sm btn-custom px-2 py-2 m-0 d-inline-flex align-items-center gap-2">
                        <span className="exhibit-icon p16">📷</span>
                            Add Images
                        </label>
                    </div>
        
                    {exhibit.images.length > 0 && (
                    <div className="exhibit-image-preview">
                        {exhibit.images.map((image) => (
                            <div key={image.id} className="exhibit-image-item position-relative border rounded-2 overflow-hidden bg-white">
                                <img src={image.preview || image.path} alt={image.name} className="exhibit-image w-100 h-100 d-block object-cover" />
                                <button type="button" className="exhibit-image-remove position-absolute text-white border-0 rounded-pill p12 d-flex align-items-center justify-content-center" onClick={() => handleImageRemove(exhibit.id, image.id)} title="Remove image" >✕</button>
                                <span className="exhibit-image-name d-block p-2 p12 text-black text-opacity-50 text-center text-break">{image.name}</span>
                            </div>
                        ))}
                    </div>
                    )}
                </div>

                {/* Textarea Column */}
                <div className="exhibit-textarea-column">
                    <label className="exhibit-column-label d-block fw-bold text-black text-opacity-75 mb-2 p14">Text Areas</label>
                    {exhibit.textareas.map((textarea, index) => (
                    <div key={textarea.id} className="exhibit-textarea-item mb-2">
                        <div className="exhibit-textarea-header d-flex justify-content-between align-items-center mb-2">
                            <label className="fw-normal text-black text-opacity-50 p14">Text Area {index + 1}</label>
                            <button type="button" className="exhibit-textarea-remove btn btn-danger text-sm px-2 py-1 m-0 rounded-circle p12" onClick={() => removeTextarea(exhibit.id, textarea.id)} disabled={exhibit.textareas.length === 1} title="Remove text area">✕</button>
                        </div>
                        <div className="quill-editor-container ">
                            <ReactQuill className="bg-white" theme="snow" value={textarea.content} onChange={(content) => handleTextareaChange(exhibit.id, textarea.id, content)} modules={quillModules} formats={quillFormats} placeholder="Enter description..." readOnly={false} />
                        </div>
                    </div>
                    ))}
                    <button type="button" className="exhibit-add-textarea btn btn-primary text-sm btn-custom p-2 m-0 w-100" onClick={() => addTextarea(exhibit.id)} >+ Add Text Area</button>
                </div>
            </div>
        </div>
        ))}
        <button type="button" className="exhibit-add-item btn btn-primary text-sm btn-custom px-2 py-3 m-0 w-100" onClick={addExhibit}>+ Add Exhibit</button>
    </div>
</div>
);
};

export default Exhibit;